import {
  CUISINE_LABELS,
  DIETARY_OPTION_LABELS,
  SERVICE_TYPE_LABELS,
  formatEur,
  type RfqSupplierView,
} from '@caterinx/shared-types';

import { formatDate } from '../lib/rfq.js';

/**
 * Die Eckdaten einer Ausschreibung samt Ausschreibungstext — gemeinsam für die Angebotsmaske
 * (`SupplierRfqDetailPage`) und die Einladungsseite (`ExternalInvitePage`).
 *
 * Beide zeigen **dieselbe Grenze** (§5, D17): PLZ und Stadt, keine Straße, kein Kundenname.
 * Zwei Kopien dieser Darstellung wären die Stelle, an der eine Seite irgendwann mehr zeigt als
 * die andere. Deshalb nimmt die Komponente nur die Felder, die beide Sichten gemeinsam haben.
 */
export type RfqFactsData = Pick<
  RfqSupplierView,
  | 'eventDate'
  | 'eventStartTime'
  | 'eventDurationMinutes'
  | 'guestCount'
  | 'postalCode'
  | 'city'
  | 'serviceTypes'
  | 'cuisines'
  | 'dietaryOptions'
  | 'dietaryCounts'
  | 'needsStaff'
  | 'needsTableware'
  | 'needsDelivery'
  | 'notes'
  | 'brief'
>;

/* eslint-disable security/detect-object-injection -- Enum-Werte, serverseitig gegen Zod validiert */

export function RfqFacts({
  rfq,
  travelSurchargeCents = 0,
}: {
  rfq: RfqFactsData;
  /** Gibt es erst nach dem Annehmen der Einladung — die Vorschau hat das Feld nicht. */
  travelSurchargeCents?: number;
}) {
  return (
    <div className="space-y-4">
      <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
        <Row label="Termin" value={`${formatDate(rfq.eventDate)}, ${rfq.eventStartTime} Uhr`} />
        <Row label="Personen" value={String(rfq.guestCount)} />
        <Row label="Ort" value={`${rfq.postalCode} ${rfq.city}`} />
        <Row label="Bewirtung" value={rfq.serviceTypes.map((type) => SERVICE_TYPE_LABELS[type]).join(', ')} />
        {rfq.eventDurationMinutes !== null && (
          <Row label="Dauer" value={`${Math.round((rfq.eventDurationMinutes / 60) * 10) / 10} Stunden`} />
        )}
        {rfq.cuisines.length > 0 && (
          <Row label="Küche" value={rfq.cuisines.map((entry) => CUISINE_LABELS[entry]).join(', ')} />
        )}
        {rfq.dietaryOptions.length > 0 && (
          <Row
            label="Ernährung"
            value={rfq.dietaryOptions
              .map((option) => {
                const count = rfq.dietaryCounts[option];
                return count ? `${DIETARY_OPTION_LABELS[option]} (${count})` : DIETARY_OPTION_LABELS[option];
              })
              .join(', ')}
          />
        )}
        <Row
          label="Gewünscht"
          value={
            [
              rfq.needsStaff ? 'Servicepersonal' : null,
              rfq.needsTableware ? 'Geschirr und Besteck' : null,
              rfq.needsDelivery ? 'Anlieferung' : null,
            ]
              .filter(Boolean)
              .join(', ') || '—'
          }
        />
        {travelSurchargeCents > 0 && <Row label="Ihr Anfahrtszuschlag" value={formatEur(travelSurchargeCents)} />}
        {rfq.notes.trim() !== '' && <Row label="Hinweise des Kunden" value={rfq.notes} full />}
      </dl>

      {rfq.brief && rfq.brief.requirementList.length > 0 && (
        <div>
          <p className="mb-1 text-sm font-medium">Anforderungen</p>
          <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
            {rfq.brief.requirementList.map((entry) => (
              <li key={entry}>{entry}</li>
            ))}
          </ul>
        </div>
      )}

      {rfq.brief && rfq.brief.openQuestions.length > 0 && (
        <div>
          <p className="mb-1 text-sm font-medium">Bitte im Angebot klären</p>
          <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
            {rfq.brief.openQuestions.map((entry) => (
              <li key={entry}>{entry}</li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-muted-foreground text-xs">
        Die genaue Anschrift und die Kontaktdaten des Kunden erhalten Sie mit dem Zuschlag.
      </p>
    </div>
  );
}

/* eslint-enable security/detect-object-injection */

export function Row({ label, value, full = false }: { label: string; value: string; full?: boolean }) {
  return (
    <div className={full ? 'sm:col-span-2' : undefined}>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-sm whitespace-pre-wrap">{value}</dd>
    </div>
  );
}
